// FLAG:useDocuAi — Documents LIST-VIEW analysis-state cell ("Prüfung" column). New module, only ever
// referenced by the gated column injector + the file-flat-list `components` map, so it is inert
// flag-OFF. A compact analog of the thumbnail tile: a status dot carrying the same blue AI sparkle,
// pulsing (white sparkle on the blue dot) + animated analysis-in-progress label, then a
// coloured dot with the blue AI mark + label ("Zu prüfen" / "Geprüft" / "E-Rechnung ungültig").
// Same underlying state source as the tile (getInvoiceAnalysisState).
import React from 'react'
import clsx from 'clsx'
import Tooltip from '@mui/material/Tooltip'
import { makeStyles } from '@mui/styles'

import SvgIcon from '@/modules/base/components/svg-icon/svg-icon'
// FLAG:useDocuAi — the same AI sparkle used by the thumbnail tile's corner mark.
import IconAi from '@assets/images/icons/ai-sparkle.svg'
import { INVOICE_ANALYSIS_STATE } from '@/modules/document/utils/invoice-analysis-state'
import { useAnalysisBlinkCap } from '@/modules/document/utils/use-analysis-blink-cap'
import { getAnalysisCellView, shouldTickLiveDerivedAnalysisCell } from './invoice-analysis-status-cell-view'

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    gap: theme.spacing(1),
    minWidth: 0,
    '&.clickable': {
      cursor: 'pointer',
    },
  },
  // Slightly larger circle so the AI sparkle sits inside it (mirrors the tile, which carries a small
  // blue AI mark on the finished status badge).
  dot: {
    width: 16,
    height: 16,
    borderRadius: '50%',
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.green,
  },
  // AI sparkle inside the dot. Blue on the (light) terminal status colours; the analysing state uses
  // the white variant below since its background is the blue primary.
  aiMark: {
    display: 'flex',
    lineHeight: 0,
    pointerEvents: 'none',
    color: theme.blue,
    '& svg': { width: 10, height: 10, color: theme.blue },
    '& svg path': { fill: theme.blue },
  },
  aiMarkOnDark: {
    color: theme.white,
    '& svg': { color: theme.white },
    '& svg path': { fill: theme.white },
  },
  review: {
    backgroundColor: theme.colors.yellow,
  },
  // "Teilweise geprüft" still needs attention → keep it on the review colour.
  partial: {
    backgroundColor: theme.colors.yellow,
  },
  analyzing: {
    backgroundColor: theme.primary,
  },
  // FLAG:useDocuAi — non-terminal transient retry (plan docuai-analysis-retrying). Copied from the
  // thumbnail tile's `analysisStatusButtonRetrying`: the same blue `theme.primary` as ANALYZING but
  // dimmed to 0.65 — calm, and never orange/red, because "delayed — will be retried automatically"
  // is a waiting state, not activity and not a failure. Without this rule the cell fell through to
  // the default green dot and paired "finished" green with a label that says the opposite.
  retrying: {
    backgroundColor: theme.primary,
    opacity: 0.65,
  },
  invalid: {
    backgroundColor: theme.error,
  },
  // FLAG:useDocuAi — observed technical failure of the analysis. Orange, exactly like the tile's
  // FAILED badge (analysisStatusButtonFailed) — deliberately NOT the red `invalid`, which is the
  // verdict "this e-invoice is invalid". Otherwise the cell would still fall through to the
  // default green dot and paired it with the label "Analyse fehlgeschlagen".
  failed: {
    backgroundColor: theme.orange,
  },
  // FLAG:useDocuAi — subtle terminal states without a result: muted gray, deliberately
  // NOT red (a timeout / "no receipt" is not an observed failure — locked decision,
  // plans/ausgaben-analyse-terminal-states).
  aborted: {
    backgroundColor: theme.textLight,
  },
  noInvoice: {
    backgroundColor: theme.textLight,
  },
  // FLAG:useDocuAi — the list-view "something is happening" motion: a calm pulsing dot
  // (distinct from the tile's blinking AI sparkle) for the duration of the analysis. Pure CSS
  // keyframes → animates while the cell is mounted, independent of ag-Grid setData refreshes.
  '@keyframes analysisPulse': {
    '0%, 100%': { opacity: 1, transform: 'scale(1)' },
    '50%': { opacity: 0.3, transform: 'scale(0.6)' },
  },
  pulse: {
    animation: '$analysisPulse 1.1s ease-in-out infinite',
  },
  label: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // Animated trailing "..." after the "In Analyse" label — reveals the three dots in sequence so
  // the text itself reads as in-progress. Rendered as its own element (not a pseudo-element) so the
  // keyframe reference is top-level and statically analysable.
  // @keyframes is not a class; the plugin cannot see it referenced from the `animation:` shorthand.
  // eslint-disable-next-line mui-unused-classes/unused-classes
  '@keyframes analysisEllipsis': {
    to: { width: '1.25em' },
  },
  ellipsisDots: {
    display: 'inline-block',
    verticalAlign: 'bottom',
    overflow: 'hidden',
    width: 0,
    whiteSpace: 'nowrap',
    animation: '$analysisEllipsis 1.6s steps(4, end) infinite',
  },
  empty: {
    color: theme.textLight,
  },
}))

const InvoiceAnalysisStatusCell = ({ data, value = null, onClick = null, liveDerived = false }) => {
  const classes = useStyles()
  const { state, label, isAnalyzing, isRetrying, hasValue } = getAnalysisCellView(data, value)
  // The dot/ellipsis animation is pure CSS. Re-render the derived Documents state while it runs so
  // the central 45-second cap is actually observed even when no websocket/store update arrives.
  useAnalysisBlinkCap(shouldTickLiveDerivedAnalysisCell(liveDerived, { isAnalyzing, isRetrying }))
  // FLAG:useDocuAi — the dot backgrounds that need the WHITE sparkle variant: both blue
  // `theme.primary` states. Keyed on the two states rather than on `isAnalyzing` alone, because
  // RETRYING shares the blue dot but deliberately not the motion — see getAnalysisCellView.
  const isOnDarkDot = isAnalyzing || isRetrying

  if (!hasValue) {
    return <span className={classes.empty}>-</span>
  }

  return (
    <Tooltip title={label}>
      <span className={clsx(classes.root, { clickable: !!onClick })} onClick={() => onClick?.(data)}>
        <span
          className={clsx(classes.dot, {
            [classes.analyzing]: state === INVOICE_ANALYSIS_STATE.ANALYZING,
            [classes.retrying]: state === INVOICE_ANALYSIS_STATE.RETRYING,
            // Motion is ANALYZING-only on purpose: RETRYING is a waiting state, so its dot stays
            // still (and its label gets no animated ellipsis below), exactly like the tile's blink.
            [classes.pulse]: isAnalyzing,
            [classes.review]: state === INVOICE_ANALYSIS_STATE.REVIEW,
            [classes.partial]: state === INVOICE_ANALYSIS_STATE.PARTIAL,
            [classes.invalid]: state === INVOICE_ANALYSIS_STATE.INVALID,
            [classes.failed]: state === INVOICE_ANALYSIS_STATE.FAILED,
            [classes.aborted]: state === INVOICE_ANALYSIS_STATE.ABORTED,
            [classes.noInvoice]: state === INVOICE_ANALYSIS_STATE.NO_INVOICE,
          })}
        >
          <span className={clsx(classes.aiMark, { [classes.aiMarkOnDark]: isOnDarkDot })}>
            <SvgIcon svg={IconAi} width="10px" height="10px" />
          </span>
        </span>
        <span className={classes.label}>
          {label}
          {isAnalyzing && <span className={classes.ellipsisDots}>...</span>}
        </span>
      </span>
    </Tooltip>
  )
}

export default InvoiceAnalysisStatusCell
